import {
  createRootRouteWithContext,
  useNavigate,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import {
  appManagementStrings,
  BmweMenuItem,
  CHOICES_PERMISSIONS,
  choiceStrings,
  commonStrings,
  COMPANIES_PERMISSIONS,
  companyStrings,
  COUNTRIES_PERMISSIONS,
  countryStrings,
  DOSSIER_PERMISSIONS,
  dossierStrings,
  EntityRoutesRecord,
  FEES_PERMISSIONS,
  feeStrings,
  getPermissionSet,
  GOODS_PERMISSIONS,
  goodStrings,
  GROUP_MANAGEMENT_PERMISSIONS,
  MIGRATION_INCONSISTENCY_PERMISSIONS,
  Root,
  RouterContext,
  USER_MANAGEMENT_PERMISSIONS,
} from '@bmwe/ui';
import { ListFeesRoute } from './stammdaten/gebuehren';
import { ListGoodsRoute } from './stammdaten/gueter';
import { ListCompaniesRoute } from './stammdaten/firmen';
import { ListCountriesRoute } from './stammdaten/laender';
import { ListAllDossiersRoute } from './vorgaenge/(listen)/alle';
import Me from '../components/me/Me';
import { ViewGoodRoute } from './stammdaten/gueter/$goodId';
import { EditGoodRoute } from './stammdaten/gueter/$goodId_.bearbeiten';
import { EditDossierRoute } from './vorgaenge/$dossierId_.bearbeiten';
import { ViewDossierRoute } from './vorgaenge/$dossierId';
import { ListMyDossiersRoute } from './vorgaenge/(listen)/meine';
import { ListOurDossiersRoute } from './vorgaenge/(listen)/unsere';
import { StammdatenOverviewRoute } from './stammdaten';
import { ChoicesOverviewRoute } from './auswahlfelder';
import { KassenzeichenRoute } from './auswahlfelder/kassenzeichen';
import { ListUsersRoute } from './verwaltung/benutzer';
import { ViewUserRoute } from './verwaltung/benutzer/$userId';
import { ViewGroupRoute } from './verwaltung/gruppen/$groupId';
import { ListGroupsRoute } from './verwaltung/gruppen';
import { MigrationInconsistenciesRoute } from './verwaltung/migrationsfehler';

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const navigate = useNavigate();

  const items: BmweMenuItem[] = [
    {
      label: feeStrings.entityPlural,
      routePath: ListFeesRoute.fullPath,
      command: () => navigate({ to: ListFeesRoute.to }),
      permission: getPermissionSet(FEES_PERMISSIONS, 'READ'),
    },
    {
      label: goodStrings.entityPlural,
      routePath: ListGoodsRoute.fullPath,
      command: () => navigate({ to: ListGoodsRoute.to }),
      permission: getPermissionSet(GOODS_PERMISSIONS, 'READ'),
    },
    {
      label: companyStrings.entityPlural,
      routePath: ListCompaniesRoute.fullPath,
      command: () => navigate({ to: ListCompaniesRoute.to }),
      permission: getPermissionSet(COMPANIES_PERMISSIONS, 'READ'),
    },
    {
      label: countryStrings.entityPlural,
      routePath: ListCountriesRoute.fullPath,
      command: () => navigate({ to: ListCountriesRoute.to }),
      permission: getPermissionSet(COUNTRIES_PERMISSIONS, 'READ'),
    },
    {
      separator: true,
    },
    {
      label: dossierStrings.listName.my,
      routePath: ListMyDossiersRoute.fullPath,
      command: () => navigate({ to: ListMyDossiersRoute.fullPath }),
      permission: getPermissionSet(DOSSIER_PERMISSIONS, 'READ'),
    },
    {
      label: dossierStrings.listName.our,
      routePath: ListOurDossiersRoute.fullPath,
      command: () => navigate({ to: ListOurDossiersRoute.fullPath }),
      permission: getPermissionSet(DOSSIER_PERMISSIONS, 'READ'),
    },
    {
      label: dossierStrings.listName.all,
      routePath: ListAllDossiersRoute.fullPath,
      command: () => navigate({ to: ListAllDossiersRoute.fullPath }),
      permission: getPermissionSet(DOSSIER_PERMISSIONS, 'READ'),
    },
    {
      separator: true,
    },
    {
      label: commonStrings.words.baseData,
      routePath: StammdatenOverviewRoute.fullPath,
      command: () => navigate({ to: StammdatenOverviewRoute.to }),
    },
    {
      label: choiceStrings.entityPlural,
      routePath: ChoicesOverviewRoute.fullPath,
      command: () => navigate({ to: ChoicesOverviewRoute.to }),
      permission: getPermissionSet(CHOICES_PERMISSIONS, 'READ'),
    },
    {
      label: choiceStrings.choiceNames.Kassenzeichen,
      routePath: KassenzeichenRoute.fullPath,
      command: () => navigate({ to: KassenzeichenRoute.to }),
      permission: getPermissionSet(CHOICES_PERMISSIONS, 'READ'),
    },
    {
      separator: true,
    },
    {
      label: appManagementStrings.user.areaName,
      routePath: ListUsersRoute.fullPath,
      command: () => navigate({ to: ListUsersRoute.to }),
      permission: getPermissionSet(USER_MANAGEMENT_PERMISSIONS, 'EDIT'),
    },
    {
      label: appManagementStrings.groups.areaName,
      routePath: ListGroupsRoute.fullPath,
      command: () => navigate({ to: ListGroupsRoute.to }),
      permission: getPermissionSet(GROUP_MANAGEMENT_PERMISSIONS, 'EDIT'),
    },
    {
      label: appManagementStrings.migration.areaName,
      routePath: MigrationInconsistenciesRoute.fullPath,
      command: () => navigate({ to: MigrationInconsistenciesRoute.to }),
      permission: getPermissionSet(MIGRATION_INCONSISTENCY_PERMISSIONS, 'READ'),
    },
  ];

  const entityRoutes: EntityRoutesRecord = {
    GOODS_EDIT: {
      route: EditGoodRoute,
      key: 'goodId',
    },
    GOODS_VIEW: {
      route: ViewGoodRoute,
      key: 'goodId',
    },
    DOSSIERS_EDIT: {
      route: EditDossierRoute,
      key: 'dossierId',
    },
    DOSSIERS_VIEW: {
      route: ViewDossierRoute,
      key: 'dossierId',
    },
    USERS_VIEW: {
      route: ViewUserRoute,
      key: 'userId',
    },
    GROUPS_VIEW: {
      route: ViewGroupRoute,
      key: 'groupId',
    },
  };

  return (
    <>
      <Root user={<Me />} items={items} entityRoutes={entityRoutes} />
      <TanStackRouterDevtools />
    </>
  );
}
